import React from 'react';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Archive, KeyRound, Mail, RotateCcw, Shield, Trash2 } from 'lucide-react';

export default function AccountRecoveryChooseStep({
  accounts, archiveIds, toggleArchive,
  targetUserId, setTargetUserId,
  mode, setMode, reason, setReason,
  notifyMember, setNotifyMember,
  confirmed, setConfirmed,
  lastReason, email, ROLE_COLORS
}) {
  const target = accounts.find((a) => a.user_id === targetUserId);
  return (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold">
                {accounts.length} compte(s) trouvé(s) — choisissez le principal à conserver
              </Label>
              <div className="mt-2 space-y-2">
                {accounts.map((a) => (
                  <div
                    key={a.user_id}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      targetUserId === a.user_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    } ${a.is_super_admin ? 'opacity-60' : ''}`}
                    onClick={() => !a.is_super_admin && setTargetUserId(a.user_id)}
                    data-testid={`recovery-account-${a.user_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        checked={targetUserId === a.user_id}
                        onChange={() => setTargetUserId(a.user_id)}
                        disabled={a.is_super_admin}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">
                            {a.prenom || ''} {a.nom || '(sans nom)'}
                          </span>
                          {a.role && (
                            <Badge className={`text-[10px] ${ROLE_COLORS[a.role] || 'bg-slate-100 text-slate-700'}`}>
                              {a.role}
                            </Badge>
                          )}
                          {a.is_super_admin && (
                            <Badge className="bg-rose-600 text-white text-[10px] font-semibold">
                              <Shield className="w-3 h-3 mr-0.5" /> non éligible
                            </Badge>
                          )}
                          {!a.is_active && (
                            <Badge className="bg-slate-700 text-white font-semibold">
                              <Archive className="w-3 h-3 mr-0.5" /> archivé
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 truncate">{a.email}</div>
                        {a.entreprise && (
                          <div className="text-xs text-slate-500">{a.entreprise}</div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-1 font-mono">
                          {a.user_id.slice(0, 8)}… · créé le {(a.created_at || '').slice(0, 10)}
                        </div>
                        <div className="flex gap-2 mt-1 text-[10px] text-slate-500">
                          <span>📄 {a.assets_count?.documents || 0}</span>
                          <span>📋 {a.assets_count?.souscriptions || 0}</span>
                          <span>🎫 {a.assets_count?.adhesions || 0}</span>
                          <span>💳 {a.assets_count?.payments || 0}</span>
                        </div>
                      </div>
                      {targetUserId !== a.user_id && !a.is_super_admin && (
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-2">
                          <Checkbox
                            checked={archiveIds.includes(a.user_id)}
                            onCheckedChange={() => toggleArchive(a.user_id)}
                            data-testid={`archive-checkbox-${a.user_id}`}
                          />
                          archiver
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-slate-200" />

            <div>
              <Label className="text-sm font-semibold">Méthode de reconstruction</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className={`border rounded-lg p-3 cursor-pointer flex items-start gap-2 ${
                  mode === 'magic_link' ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                }`} data-testid="mode-magic-link">
                  <input type="radio" checked={mode === 'magic_link'} onChange={() => setMode('magic_link')} className="mt-1" />
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Magic-link</div>
                    <div className="text-xs text-slate-500">Lien sécurisé 7 jours. Recommandé.</div>
                  </div>
                </label>
                <label className={`border rounded-lg p-3 cursor-pointer flex items-start gap-2 ${
                  mode === 'temp_password' ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                }`} data-testid="mode-temp-password">
                  <input type="radio" checked={mode === 'temp_password'} onChange={() => setMode('temp_password')} className="mt-1" />
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Mot de passe temporaire</div>
                    <div className="text-xs text-slate-500">L&apos;admin transmet hors-bande.</div>
                  </div>
                </label>
              </div>
              {/* Mode destructif : libération définitive de l'email */}
              <label className={`mt-2 block border-2 rounded-lg p-3 cursor-pointer flex items-start gap-2 ${
                mode === 'release_email' ? 'border-rose-500 bg-rose-50' : 'border-rose-200 bg-rose-50/30 hover:bg-rose-50'
              }`} data-testid="mode-release-email">
                <input type="radio" checked={mode === 'release_email'} onChange={() => setMode('release_email')} className="mt-1" />
                <div className="flex-1">
                  <div className="font-semibold text-sm flex items-center gap-1 text-rose-800">
                    <Trash2 className="w-3.5 h-3.5" /> Libérer définitivement l&apos;email (purge complète)
                  </div>
                  <div className="text-xs text-rose-700 mt-0.5">
                    L&apos;email est renommé en <code>released-XXX-...</code> côté base, le compte est désactivé.
                    L&apos;adresse devient <strong>libre pour une nouvelle inscription immédiatement</strong>.
                    Les factures, adhésions et documents restent conservés sous l&apos;ID interne (audit + RGPD).
                  </div>
                </div>
              </label>
            </div>

            <div>
              <Label htmlFor="rec-reason">Raison <span className="text-rose-600">*</span></Label>
              <Textarea
                id="rec-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Compte verrouillé, doublon, oubli de mot de passe..."
                rows={2}
                className={`mt-1 ${reason.trim().length > 0 && reason.trim().length < 10 ? 'border-amber-400 focus:border-amber-500' : ''}`}
                data-testid="recovery-reason"
              />
              <p className={`text-xs mt-1 ${reason.trim().length < 10 ? 'text-amber-700 font-medium' : 'text-emerald-700'}`}>
                {reason.trim().length}/10 caractères min.
                {reason.trim().length < 10 ? ' — saisissez une raison plus précise pour activer le bouton.' : ' ✓ — journalisé dans l\'audit.'}
              </p>
              {reason.trim().length === 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="recovery-reason-templates">
                  {lastReason && lastReason.length >= 10 && (
                    <button
                      type="button"
                      onClick={() => setReason(lastReason)}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-violet-400 bg-violet-50 hover:bg-violet-100 text-violet-800 font-medium transition-colors flex items-center gap-1"
                      data-testid="recovery-reason-last"
                      title="Réutiliser la dernière raison validée"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Dernière : « {lastReason.length > 32 ? lastReason.slice(0, 32) + '…' : lastReason} »
                    </button>
                  )}
                  {[
                    'Compte verrouillé — utilisateur bloqué',
                    'Oubli de mot de passe sans accès email',
                    'Doublon archivé — fusion sur ce compte',
                    'Changement de statut',
                  ]
                    .filter((tpl) => tpl !== lastReason)
                    .map((tpl) => (
                      <button
                        key={tpl}
                        type="button"
                        onClick={() => setReason(tpl)}
                        className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50 hover:bg-blue-50 hover:border-blue-300 text-slate-600 transition-colors"
                        data-testid={`recovery-reason-template-${tpl.slice(0, 10).toLowerCase().replace(/\s/g, '-')}`}
                      >
                        + {tpl}
                      </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="notify"
                checked={notifyMember}
                onCheckedChange={setNotifyMember}
                data-testid="recovery-notify"
              />
              <Label htmlFor="notify" className="text-sm cursor-pointer">
                Notifier l'utilisateur par email
              </Label>
            </div>

            <div className={`border rounded-md p-3 text-xs space-y-1 ${
              mode === 'release_email' ? 'bg-rose-50 border-rose-300' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className={`font-semibold mb-1 ${mode === 'release_email' ? 'text-rose-900' : 'text-slate-700'}`}>
                Récapitulatif
              </div>
              {mode === 'release_email' ? (
                <>
                  <div>• Email à libérer : <strong>{target?.email}</strong></div>
                  <div>• Compte cible : <strong>désactivé (renommé released-…)</strong></div>
                  <div>• Comptes archivés : <strong>{archiveIds.length}</strong></div>
                  <div>• Mode : <strong className="text-rose-700">Libération définitive (purge)</strong></div>
                  <div>• Tokens anciens : <strong>tous invalidés</strong></div>
                  <div>• Données métier : <strong>conservées (audit + RGPD)</strong></div>
                  <div>• Ré-inscription : <strong className="text-emerald-700">possible immédiatement</strong></div>
                </>
              ) : (
                <>
                  <div>• Compte conservé : <strong>{target?.email}</strong> ({target?.role})</div>
                  <div>• Comptes archivés : <strong>{archiveIds.length}</strong></div>
                  <div>• Mode : <strong>{mode === 'magic_link' ? 'Magic-link 7 jours' : 'Mot de passe temporaire'}</strong></div>
                  <div>• Tokens anciens : <strong>tous invalidés</strong></div>
                  <div>• Données métier : <strong>conservées</strong></div>
                </>
              )}
            </div>

            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <Checkbox
                id="confirm"
                checked={confirmed}
                onCheckedChange={setConfirmed}
                className="mt-0.5"
                data-testid="recovery-confirm"
              />
              <span className="text-sm text-slate-700">
                Je confirme avoir vérifié l'identité de l'utilisateur et autoriser cette action.
              </span>
            </label>
          </div>
  );
}
